/**
 * SH Update: License Upsell System
 *
 * This file handles the license upsell checkbox functionality for the cart.
 *
 * NOTE: Package auto-add functionality has been removed.
 * Course and Kit are now added post-purchase via Shopify Flow.
 * See README.md for details on the new system.
 */

class SHPackageSystem {
    constructor() {
        this.selectors = {
            // License upsell selectors only
            licenseCheckbox: '.license-upsell-checkbox',
            licenseError: '.cart__license-upsell-error',
            validationError: '#license-validation-error',
            checkoutButton: 'button[name="checkout"], #checkout'
        };

        this.isUpdating = false; // Lock to prevent race conditions
        this.init();
    }

    init() {
        // Only license upsell functionality - no package interception
        this.bindLicenseCheckbox();
        this.validateLicenseCheckout();
        this.bindCheckoutButtons();

        // Listen for global cart updates (Theme PubSub)
        if (window.PUB_SUB_EVENTS && window.subscribe) {
            window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, () => {
                this.bindLicenseCheckbox();
                this.validateLicenseCheckout();
            });
        }
    }

    // ========================================
    // UI Update Methods (used by license system)
    // ========================================

    updateUI(data) {
        console.log('[SH License] updateUI called with data:', data);
        console.log('[SH License] Sections available:', data.sections ? Object.keys(data.sections) : 'none');

        // Handle cart drawer
        const isDrawer = document.querySelector('cart-drawer');
        if (isDrawer) {
            console.log('[SH License] Updating cart drawer');
            isDrawer.renderContents(data);
        }

        // For main cart page, directly trigger the cart-items component's update method
        const cartItems = document.querySelector('cart-items');
        if (cartItems && typeof cartItems.onCartUpdate === 'function') {
            console.log('[SH License] Directly calling cart-items onCartUpdate()');
            cartItems.onCartUpdate();
        }

        // Publish event for other listeners (if any)
        if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
                source: 'sh-license-system',
                cartData: data
            });
        }

        // Update cart icon - use sections data if available, otherwise fetch
        if (data.sections && data.sections['cart-icon-bubble']) {
            console.log('[SH License] Updating cart icon from sections data');
            const parser = new DOMParser();
            const doc = parser.parseFromString(data.sections['cart-icon-bubble'], 'text/html');
            const newBubble = doc.querySelector('.shopify-section');
            const currentBubble = document.getElementById('cart-icon-bubble');
            if (currentBubble && newBubble) {
                currentBubble.innerHTML = newBubble.innerHTML;
            }
        } else {
            this.refreshCartIcon();
        }

        // Update cart notification sections if notification is open
        const cartNotification = document.querySelector('cart-notification');
        const notification = cartNotification ? cartNotification.querySelector('#cart-notification') : null;
        const notificationIsActive = notification && notification.classList.contains('active');

        console.log('[SH License] Cart notification check:', {
            exists: !!cartNotification,
            isActive: notificationIsActive,
            hasSections: !!(data.sections)
        });

        if (notificationIsActive && data.sections) {
            console.log('[SH License] Cart notification is open, updating sections');

            // Update cart-notification-button section
            if (data.sections['cart-notification-button']) {
                const buttonElement = document.getElementById('cart-notification-button');
                if (buttonElement) {
                    const parser = new DOMParser();
                    const parsedHTML = parser.parseFromString(data.sections['cart-notification-button'], 'text/html');
                    const newButtonSection = parsedHTML.querySelector('.shopify-section');
                    if (newButtonSection) {
                        buttonElement.innerHTML = newButtonSection.innerHTML;
                        console.log('[SH License] Cart notification button text updated to:', newButtonSection.innerHTML.trim());
                    }
                }
            }

            // Update cart-notification-product if available
            if (data.sections['cart-notification-product'] && cartNotification.cartItemKey) {
                const productElement = document.getElementById('cart-notification-product');
                if (productElement) {
                    const parser = new DOMParser();
                    const parsedHTML = parser.parseFromString(data.sections['cart-notification-product'], 'text/html');
                    const selector = `[id="cart-notification-product-${cartNotification.cartItemKey}"]`;
                    const newProductSection = parsedHTML.querySelector(selector);
                    if (newProductSection) {
                        productElement.innerHTML = newProductSection.innerHTML;
                        console.log('[SH License] Cart notification product updated');
                    }
                }
            }
        }

        // Refresh cart footer (only needed for main cart page)
        this.refreshCartFooter();
    }

    refreshCartIcon() {
        // Fetch and update cart icon bubble
        fetch(`${window.Shopify.routes.root}?section_id=cart-icon-bubble`)
            .then(response => response.text())
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newBubble = doc.querySelector('.shopify-section');
                const currentBubble = document.getElementById('cart-icon-bubble');
                if (currentBubble && newBubble) {
                    currentBubble.innerHTML = newBubble.innerHTML;
                    console.log('[SH License] Cart icon updated');
                }
            })
            .catch(err => console.error('[SH License] Error refreshing cart icon:', err));
    }

    refreshCartFooter() {
        // Fetch and update cart footer
        const cartFooter = document.getElementById('main-cart-footer');
        if (!cartFooter || !cartFooter.dataset.id) return;

        fetch(`${window.Shopify.routes.root}cart?section_id=${cartFooter.dataset.id}`)
            .then(response => response.text())
            .then(html => {
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, 'text/html');
                const newFooter = doc.getElementById('main-cart-footer');
                if (newFooter) {
                    cartFooter.innerHTML = newFooter.innerHTML;

                    // Update classes
                    if (newFooter.classList.contains('is-empty')) {
                        cartFooter.classList.add('is-empty');
                    } else {
                        cartFooter.classList.remove('is-empty');
                    }

                    // Re-execute scripts
                    const scripts = cartFooter.querySelectorAll('script');
                    scripts.forEach(oldScript => {
                        const newScript = document.createElement('script');
                        let scriptContent = oldScript.textContent;
                        scriptContent = scriptContent.replace(
                            /document\.addEventListener\s*\(\s*["']DOMContentLoaded["']\s*,\s*\(\s*\)\s*=>\s*\{/g,
                            '(function() {'
                        );
                        scriptContent = scriptContent.replace(/\}\s*\)\s*;?\s*$/, '})();');
                        newScript.textContent = scriptContent;
                        oldScript.parentNode.replaceChild(newScript, oldScript);
                    });

                    console.log('[SH License] Cart footer updated');
                }
            })
            .catch(err => console.error('[SH License] Error refreshing cart footer:', err));
    }

    // ========================================
    // License Upsell Methods
    // ========================================

    /**
     * Bind license upsell checkbox using event delegation
     * SH Updated: January 2026 - Switched to event delegation for dynamic elements
     */
    bindLicenseCheckbox() {
        // Only bind once to document
        if (this.licenseCheckboxBound) return;
        this.licenseCheckboxBound = true;

        // Debounced handler
        const debouncedHandler = this.debounce((e) => {
            this.handleLicenseCheckboxChange(e);
        }, 300);

        // Event delegation - catches all current and future checkboxes
        document.addEventListener('change', (e) => {
            if (e.target.matches(this.selectors.licenseCheckbox)) {
                debouncedHandler(e);
            }
        });

        console.log('[SH License] Checkbox event delegation bound');
    }

    /**
     * Debounce helper
     */
    debounce(func, wait) {
        let timeout;
        return function executedFunction(...args) {
            const later = () => {
                clearTimeout(timeout);
                func(...args);
            };
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
        };
    }

    /**
     * Handle license checkbox toggle
     * SH Updated: January 2026 - Added cart notification button disabling during update
     */
    async handleLicenseCheckboxChange(e) {
        if (this.isUpdating) return;

        const checkbox = e.target;
        const isChecked = checkbox.checked;
        const variantId = checkbox.dataset.variantId;

        // Show loading state
        checkbox.disabled = true;
        this.showLicenseLoading(checkbox, true);
        this.setCartNotificationButtonsDisabled(true);
        this.isUpdating = true;

        try {
            if (isChecked) {
                // Add license to cart
                await this.addLicenseToCart(variantId);
            } else {
                // Remove all licenses from cart
                await this.removeLicensesFromCart();
            }

            // Sync checkbox state across drawer and page
            this.syncLicenseCheckboxes(isChecked);

        } catch (error) {
            console.error('[SH License] Checkbox error:', error);
            // Revert checkbox state
            checkbox.checked = !isChecked;
            this.showLicenseError(checkbox, 'Unable to update cart. Please try again.');
        } finally {
            checkbox.disabled = false;
            this.showLicenseLoading(checkbox, false);
            this.setCartNotificationButtonsDisabled(false);
            this.isUpdating = false;
        }
    }

    /**
     * Disable/enable View Cart and Checkout buttons in cart notification
     * SH Updated: January 2026 - Prevents navigation during cart update
     */
    setCartNotificationButtonsDisabled(disabled) {
        const cartNotification = document.querySelector('cart-notification');
        if (!cartNotification) return;

        // View Cart link
        const viewCartLink = cartNotification.querySelector('#cart-notification-button');
        if (viewCartLink) {
            if (disabled) {
                viewCartLink.style.pointerEvents = 'none';
                viewCartLink.style.opacity = '0.5';
                viewCartLink.setAttribute('aria-disabled', 'true');
            } else {
                viewCartLink.style.pointerEvents = '';
                viewCartLink.style.opacity = '';
                viewCartLink.removeAttribute('aria-disabled');
            }
        }

        // Checkout button
        const checkoutBtn = cartNotification.querySelector('button[name="checkout"]');
        if (checkoutBtn) {
            checkoutBtn.disabled = disabled;
            if (disabled) {
                checkoutBtn.style.opacity = '0.5';
            } else {
                checkoutBtn.style.opacity = '';
            }
        }

        // Continue shopping link
        const continueLink = cartNotification.querySelector('.cart-notification__links > button.link');
        if (continueLink) {
            if (disabled) {
                continueLink.style.pointerEvents = 'none';
                continueLink.style.opacity = '0.5';
            } else {
                continueLink.style.pointerEvents = '';
                continueLink.style.opacity = '';
            }
        }

        console.log('[SH License] Cart notification buttons', disabled ? 'disabled' : 'enabled');
    }

    /**
     * Add license product to cart
     */
    async addLicenseToCart(variantId) {
        // Determine which sections to render
        const isDrawer = document.querySelector('cart-drawer');
        const isNotification = document.querySelector('cart-notification');

        let sections = ['cart-icon-bubble'];
        if (isDrawer) sections.push('cart-drawer');
        if (isNotification) sections.push('cart-notification-product', 'cart-notification-button');

        const body = JSON.stringify({
            items: [{
                id: parseInt(variantId),
                quantity: 1,
                properties: {
                    '_license_upsell': 'true'
                }
            }],
            sections: sections,
            sections_url: window.location.pathname
        });

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: body
        });

        if (!response.ok) {
            throw new Error('Failed to add license to cart');
        }

        const data = await response.json();

        // Update UI
        this.updateUI(data);

        console.log('[SH License] License added to cart');
    }

    /**
     * Remove all license products from cart
     */
    async removeLicensesFromCart() {
        // Fetch current cart
        const cartResponse = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await cartResponse.json();

        // Find all license items
        const updates = {};
        cart.items.forEach(item => {
            if (item.properties && item.properties['_license_upsell'] === 'true') {
                updates[item.key] = 0;
            }
        });

        if (Object.keys(updates).length === 0) {
            console.log('[SH License] No licenses to remove');
            return;
        }

        // Determine which sections to render
        const isDrawer = document.querySelector('cart-drawer');
        const isNotification = document.querySelector('cart-notification');

        let sections = ['cart-icon-bubble'];
        if (isDrawer) sections.push('cart-drawer');
        if (isNotification) sections.push('cart-notification-product', 'cart-notification-button');

        // Remove via cart update API
        const updateResponse = await fetch(window.Shopify.routes.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                updates,
                sections: sections,
                sections_url: window.location.pathname
            })
        });

        if (!updateResponse.ok) {
            throw new Error('Failed to remove licenses from cart');
        }

        const data = await updateResponse.json();

        // Update UI
        this.updateUI(data);

        console.log('[SH License] All licenses removed from cart');
    }

    /**
     * Validate that licenses aren't purchased alone
     * NOTE: This method ONLY shows/hides the error message.
     * It does NOT disable buttons - that's handled by the click handler in bindCheckoutButtons()
     * This prevents conflicts with other validations (like State Provider Number)
     */
    async validateLicenseCheckout() {
        const validationError = document.querySelector(this.selectors.validationError);

        if (!validationError) return;

        try {
            // Fetch current cart
            const response = await fetch(window.Shopify.routes.root + 'cart.js');
            const cart = await response.json();

            // Check if cart has only license products
            const hasLicense = cart.items.some(item =>
                item.properties && item.properties['_license_upsell'] === 'true'
            );

            const hasMainProduct = cart.items.some(item =>
                !item.properties || !item.properties['_license_upsell']
            );

            // Validation fails if only licenses in cart
            const validationFails = hasLicense && !hasMainProduct;

            if (validationFails) {
                validationError.classList.remove('hidden');
                console.log('[SH License] Validation failed: licenses only');
            } else {
                validationError.classList.add('hidden');
                console.log('[SH License] Validation passed');
            }

        } catch (error) {
            console.error('[SH License] Validation error:', error);
        }
    }

    /**
     * Bind checkout button validation using event delegation
     * SH Updated: January 2026 - Switched to event delegation for dynamic elements
     */
    bindCheckoutButtons() {
        // Only bind once to document
        if (this.checkoutButtonsBound) return;
        this.checkoutButtonsBound = true;

        // Event delegation with capture phase
        document.addEventListener('click', async (e) => {
            const button = e.target.closest(this.selectors.checkoutButton);
            if (!button) return;

            // Re-validate before allowing checkout
            await this.validateLicenseCheckout();

            // If validation error is visible, prevent checkout
            const validationError = document.querySelector(this.selectors.validationError);
            if (validationError && !validationError.classList.contains('hidden')) {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                console.log('[SH License] Checkout prevented: validation failed');

                // Scroll to error
                validationError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, true); // Use capture phase to run before other handlers

        console.log('[SH License] Checkout button event delegation bound');
    }

    /**
     * Show/hide loading state for checkbox
     */
    showLicenseLoading(checkbox, isLoading) {
        const label = checkbox.closest('.cart__license-upsell-label');
        if (!label) return;

        if (isLoading) {
            label.style.opacity = '0.5';
            label.style.pointerEvents = 'none';
        } else {
            label.style.opacity = '1';
            label.style.pointerEvents = 'auto';
        }
    }

    /**
     * Show error message near checkbox
     */
    showLicenseError(checkbox, message) {
        const errorDiv = checkbox.closest('.cart__license-upsell')?.querySelector('.cart__license-upsell-error');
        if (!errorDiv) return;

        errorDiv.textContent = message;
        errorDiv.classList.remove('hidden');

        // Auto-hide after 5 seconds
        setTimeout(() => {
            errorDiv.classList.add('hidden');
        }, 5000);
    }

    /**
     * Sync checkbox state between drawer and page
     */
    syncLicenseCheckboxes(isChecked) {
        const checkboxes = document.querySelectorAll(this.selectors.licenseCheckbox);
        checkboxes.forEach(cb => {
            if (cb.checked !== isChecked) {
                cb.checked = isChecked;
            }
        });
    }
}

// SH Updated: January 2026 - Simplified init with event delegation (no rebinding needed)
document.addEventListener('DOMContentLoaded', () => {
    window.SHPackageSystem = new SHPackageSystem();
});
